import {useState} from 'react'
import {Link, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {Tag} from '@/components/ui/tag'
import {HelpIcon} from '@/components/help-icon'
import {PageHeading} from '@/components/page-heading'
import {ListEmptyState, SectionCard, StatCard} from '@/components/section-card'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {useMiArea, useMiAreaPersonas, useRenameMiArea} from '@/hooks/empresa/organizacion/use-jerarquia'
import {translateApiError} from '@/lib/errors'

const ESTADO_TONE: Record<string, 'neutral' | 'amber' | 'green'> = {
  registro_basico: 'neutral',
  ficha_completa: 'amber',
  entrevista_completa: 'green',
}

function AreaRenameRow({businessId, areaId, name}: {businessId: string; areaId: string; name: string}) {
  const {t} = useTranslation()
  const godReadOnly = useGodModeReadOnly()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(name)
  const [error, setError] = useState<string | null>(null)
  const rename = useRenameMiArea(businessId)

  if (!editing) {
    return (
      <div className="surface-divider flex items-center justify-between gap-3 border-b px-5 py-3 last:border-b-0">
        <span className="min-w-0 font-medium break-words text-foreground">{name}</span>
        {!godReadOnly && (
          <button
            type="button"
            data-testid={`mi-area-rename-open-${areaId}`}
            onClick={() => {
              setValue(name)
              setError(null)
              setEditing(true)
            }}
            className="shrink-0 text-xs font-medium text-primary hover:underline">
            {t('jerarquia.miArea.rename')}
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="surface-divider space-y-2 border-b px-5 py-3 last:border-b-0">
      {/* En el teléfono el campo se lleva la fila y los dos botones bajan: antes
          los tres competían por 350 px y el campo quedaba de dos caracteres. */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={value}
          data-testid={`mi-area-rename-input-${areaId}`}
          onChange={(e) => setValue(e.target.value)}
          className="min-w-0 flex-1 basis-48 rounded-md border border-input bg-background px-3 py-1.5 text-base shadow-sm focus:border-ring focus:outline-none md:text-sm"
        />
        <Button
          size="sm"
          data-testid={`mi-area-rename-save-${areaId}`}
          disabled={godReadOnly || rename.isPending || !value.trim() || value.trim() === name}
          onClick={() => {
            setError(null)
            rename.mutate(
              {areaId, name: value.trim()},
              {onSuccess: () => setEditing(false), onError: (err) => setError(translateApiError(err))}
            )
          }}>
          {t('common.save')}
        </Button>
        <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
          {t('common.cancel')}
        </Button>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}

export function MiAreaPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const {data: miArea, isLoading} = useMiArea(businessId)
  const [page, setPage] = useState(1)
  const personas = useMiAreaPersonas(businessId, page)

  if (isLoading || !miArea) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-5" data-testid="mi-area-page">
      <PageHeading title={t('jerarquia.miArea.title')} />

      {/* Tres cifras, tres tarjetas — no tres columnas dentro de una. A 390 px
          la rejilla fija de 3 dejaba «Evaluación pendiente» en cuatro líneas
          sobre un número de 24 px; ahora se apilan. */}
      <div className="grid gap-4 sm:grid-cols-3" data-testid="mi-area-resumen">
        <StatCard label={t('jerarquia.miArea.personas')} value={miArea.resumen.total_personas} />
        <StatCard
          label={t('jerarquia.miArea.evaluacionPendiente')}
          value={miArea.resumen.evaluacion_pendiente}
          alert={miArea.resumen.evaluacion_pendiente > 0}
        />
        <StatCard
          label={t('jerarquia.miArea.conVencidos')}
          value={miArea.resumen.con_pendientes_vencidos}
          alert={miArea.resumen.con_pendientes_vencidos > 0}
        />
      </div>

      {/* El vacío de esta pantalla no tiene acción propia —el líder de un área lo
          asigna quien responde por el SG-SST, no quien la mira—, así que el
          «a quién pedírselo» no se borra: se agrupa en UN disparador por
          sección, en la cabecera (U4). */}
      <SectionCard
        title={t('jerarquia.miArea.areasEyebrow')}
        actions={
          <HelpIcon
            label={t('common.help.iconLabelFor', {campo: t('jerarquia.miArea.areasEyebrow')})}
            testId="mi-area-areas-tip">
            {t('jerarquia.miArea.areasTip')}
          </HelpIcon>
        }
        bodyClassName="p-0"
        className="overflow-hidden">
        {miArea.areas.length === 0 ? (
          <ListEmptyState title={t('jerarquia.miArea.noAreas')} />
        ) : (
          miArea.areas.map((area) => (
            <AreaRenameRow key={area.id} businessId={businessId!} areaId={area.id} name={area.name} />
          ))
        )}
      </SectionCard>

      <SectionCard
        title={t('jerarquia.miArea.genteEyebrow')}
        actions={
          <HelpIcon
            label={t('common.help.iconLabelFor', {campo: t('jerarquia.miArea.genteEyebrow')})}
            testId="mi-area-gente-tip">
            {t('jerarquia.miArea.genteTip')}
          </HelpIcon>
        }
        bodyClassName="p-0"
        className="overflow-hidden">
        {personas.isLoading && !personas.data ? (
          <Skeleton className="h-32 w-full" />
        ) : !personas.data || personas.data.total === 0 ? (
          <div data-testid="mi-area-empty">
            <ListEmptyState title={t('jerarquia.miArea.noPersonas')} />
          </div>
        ) : (
          <ul>
            {personas.data.items.map((persona) => (
              <li
                key={persona.id}
                data-testid={`mi-area-persona-${persona.id}`}
                className="surface-divider flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b px-5 py-3.5 transition-colors last:border-b-0 hover:bg-muted/45">
                <div className="min-w-0 basis-40 grow">
                  <Link
                    to={`/businesses/${businessId}/empleados/${persona.id}`}
                    className="font-medium break-words text-foreground hover:underline">
                    {persona.nombre_completo}
                  </Link>
                  {persona.cargo && <p className="text-xs text-muted-foreground">{persona.cargo}</p>}
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <Tag tone={ESTADO_TONE[persona.onboarding_estado] ?? 'neutral'}>
                    {t(`jerarquia.miArea.estado.${persona.onboarding_estado}`)}
                  </Tag>
                  {persona.pendientes_vencidos > 0 && (
                    <Tag tone="red">
                      {t('jerarquia.miArea.pendientesVencidos', {count: persona.pendientes_vencidos})}
                    </Tag>
                  )}
                  {persona.pendientes_por_vencer > 0 && (
                    <Tag tone="amber">
                      {t('jerarquia.miArea.pendientesPorVencer', {count: persona.pendientes_por_vencer})}
                    </Tag>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        {/* R6: 40-80 personas en un área de producción se pintaban de una. */}
        {personas.data && personas.data.total > 0 && (
          <div className="px-5 pb-4">
            <PaginationBar
              page={personas.data.page}
              pageSize={personas.data.page_size}
              total={personas.data.total}
              onPageChange={setPage}
            />
          </div>
        )}
      </SectionCard>
    </div>
  )
}
